import { useReachedTheEndListener } from 'hooks/reachedTheEnd';
import { enTypeComment } from 'interface/lessonComment';
import { useEffect, useState, useCallback } from 'react';
import { IDataWithPagination, fetchComments } from 'services/comments';
import { useParams } from 'react-router-dom';
import CommentItem from './item';
import { makeStyles, Box, Typography } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import { IComments } from 'interface/comment';
import NoCommentsImage from 'assets/img/nocomments.png';

interface IProps {
  type: enTypeComment;
  comments: Partial<IDataWithPagination>;
  setComments: React.Dispatch<React.SetStateAction<Partial<IDataWithPagination>>>;
  isSubmitting?: boolean;
}

const useStyles = makeStyles((theme) => ({
  root: {
    overflowY: 'scroll',
    maxHeight: 500,
    margin: theme.spacing(4, 0),
    '&::-webkit-scrollbar-track': {
      backgroundColor: theme.palette.grey[300],
      borderRadius: 8,
    },
    '&::-webkit-scrollbar': {
      width: 6,
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.grey[600],
      borderRadius: 8,
    },
  },
  skeleton: {
    background: theme.palette.dark[600],
    borderRadius: 8,
  },
  skeletonContainer: {
    padding: theme.spacing(6, 0),
    display: 'flex',
    flexDirection: 'column',
    '& > div:first-child': {
      '& > span:first-child': {
        borderRadius: '50%',
      },
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
  },
  emptyState: {
    marginTop: theme.spacing(4),
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    '& img': {
      maxWidth: '20%',
    },
  },
  noCommentTitle: {
    color: theme.palette.grey[500],
    fontWeight: 'bold',
    fontSize: 24,
    marginTop: theme.spacing(2),
    maxWidth: 650,
  },
  noCommentContent: {
    color: theme.palette.grey[400],
    fontSize: 16,
    marginTop: theme.spacing(2),
    maxWidth: 700,
  },
}));

const CommentList = ({ type, setComments, comments, ...props }: IProps) => {
  const [loading, setLoading] = useState(false);
  const classes = useStyles();
  const getListenerProps = useReachedTheEndListener();
  const { lessonID } = useParams<{ lessonID: string }>();

  const SkeletonItem = (
    <div className={classes.skeletonContainer}>
      <div>
        <Skeleton className={classes.skeleton} variant='circle' width={40} height={40} />
        <Box marginLeft={4}>
          <Skeleton className={classes.skeleton} variant='text' width={260} height={28} />
        </Box>
      </div>
      <Box marginTop={4}>
        <Skeleton className={classes.skeleton} variant='text' width={372} />
        <Skeleton className={classes.skeleton} variant='text' width={267} />
      </Box>
    </div>
  );

  const loadComments = useCallback(
    (params: Partial<Omit<IDataWithPagination, 'data'>> = {}) => {
      if (loading) return;
      setLoading(true);
      fetchComments({ type, lessonId: parseInt(lessonID), ...params })
        .then((newComments) => {
          setComments((comments) => ({
            ...newComments,
            data: [...comments.data, ...newComments.data],
          }));
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [lessonID, setComments, type, loading]
  );

  const setReplies = useCallback(
    (commentId: number, replies: IComments[]) => {
      setComments((comments) => {
        const data = comments.data.map((comment) => {
          if (comment.id === commentId) {
            comment.replies = replies;
          }
          return comment;
        });
        return {
          ...comments,
          data,
        };
      });
    },
    [setComments]
  );

  const resetComments = useCallback(() => {
    if (loading) return;
    setLoading(true);
    fetchComments({ type, lessonId: parseInt(lessonID) })
      .then(setComments)
      .finally(() => {
        setLoading(false);
      });
  }, [lessonID, setComments, type, loading]);

  const loadMoreComments = () => {
    const params = { ...comments };
    if (params.page >= params.totalPages) return;
    params.page = params.page + 1;
    delete params.data;
    loadComments(params);
  };

  useEffect(() => {
    setLoading(true);
    fetchComments({ type, lessonId: parseInt(lessonID) })
      .then(setComments)
      .finally(() => {
        setLoading(false);
      });
  }, [type, lessonID, setComments]);

  if (loading || props.isSubmitting) {
    return (
      <div className={classes.root}>
        {SkeletonItem}
        {SkeletonItem}
        {SkeletonItem}
      </div>
    );
  }

  if (!loading && (!comments || (comments?.data ?? []).length === 0))
    return (
      <div className={classes.emptyState} id='lesson-comments-empty'>
        <img src={NoCommentsImage} alt='nocomments' />
        <Typography align='center' className={classes.noCommentTitle}>
          Hum! Parece que você ainda não fez nenhum comentário nesta aula!
        </Typography>
        <Typography align='center' className={classes.noCommentContent}>
          Os seus comentários aparecerão aqui e você pode fazê-los usando o campo acima.
        </Typography>
        <Typography align='center' className={classes.noCommentContent}>
          Aproveite esse espaço para tirar suas dúvidas ou deixar sua opinião sobre essa aula! Isso nos ajuda a entregar
          conteúdos ainda melhores para você
        </Typography>
      </div>
    );

  return (
    <div className={classes.root} {...getListenerProps(loadMoreComments)}>
      {(comments?.data ?? []).map((comment) => (
        <CommentItem key={comment.id} {...comment} type={type} resetComments={resetComments} setReplies={setReplies} />
      ))}
      {loading && SkeletonItem}
    </div>
  );
};

export default CommentList;
